/**
 * Задачи сбора (Wordstat / Pinterest) со статусом. Сбор делает reels-worker,
 * минуту-три: экран показывает очередь, чтобы было видно — запрос принят и идёт,
 * а не потерялся. Ошибка показывается текстом целиком: по ней чинят.
 */
import { Loader2 } from "lucide-react";
import { Link } from "react-router";

import { type CollectTask, type TaskStatus } from "@/lib/trends";
import { when } from "@/lib/media";

const STATUS: Record<TaskStatus, { text: string; tone: string }> = {
  queued: { text: "в очереди", tone: "bg-neutral-bg text-fg-muted" },
  running: { text: "идёт", tone: "bg-warn-bg text-warn" },
  done: { text: "готово", tone: "bg-ok-bg text-ok" },
  failed: { text: "ошибка", tone: "bg-err-bg text-err" },
  cancelled: { text: "остановлено", tone: "bg-neutral-bg text-fg-muted" },
};

export function TaskBadge({ status }: { status: TaskStatus }) {
  const s = STATUS[status] ?? { text: status, tone: "bg-neutral-bg text-fg-muted" };
  return (
    <span
      className={`inline-flex h-5 items-center gap-1 whitespace-nowrap rounded-[4px] px-1.5 text-[11.5px] font-medium ${s.tone}`}
    >
      {status === "running" || status === "queued" ? (
        <Loader2 size={11} className={status === "running" ? "animate-spin" : ""} />
      ) : null}
      {s.text}
    </span>
  );
}

/** Чем кончился сбор — коротко, из `result` сервиса. */
function resultText(task: CollectTask): string {
  const r = task.result;
  if (!r) return "";
  if (task.kind === "wordstat") {
    const parts = [];
    if (typeof r["phrases"] === "number") parts.push(`фраз ${r["phrases"]}`);
    if (typeof r["types"] === "number") parts.push(`типов ${r["types"]}`);
    return parts.join(" · ");
  }
  if (task.kind === "pinterest_batch") {
    const parts = [];
    if (typeof r["translated"] === "number") parts.push(`переведено ${r["translated"]}`);
    if (typeof r["planned"] === "number") parts.push(`поставлено ${r["planned"]}`);
    if (typeof r["skipped"] === "number") parts.push(`пропущено ${r["skipped"]}`);
    return parts.join(" · ");
  }
  const parts = [];
  if (typeof r["seen"] === "number") parts.push(`увидено ${r["seen"]}`);
  if (typeof r["downloaded"] === "number") parts.push(`скачано ${r["downloaded"]}`);
  const failed = r["failed"];
  if (Array.isArray(failed) && failed.length) parts.push(`не скачалось ${failed.length}`);
  return parts.join(" · ");
}

/** Что именно собирали: параметры Pinterest — выдача, страницы, запрос. */
function paramsText(task: CollectTask): string {
  if (task.kind !== "pinterest") return "";
  const p = task.params;
  const parts = [];
  if (p["lang"] === "en" || p["lang"] === "ru") parts.push(p["lang"]);
  if (p["scope"] === "videos") parts.push("видео");
  else if (p["scope"] === "pins") parts.push("картинки");
  if (typeof p["pages"] === "number") parts.push(`${p["pages"]} стр.`);
  if (typeof p["top"] === "number") parts.push(`лучших ${p["top"]}`);
  if (typeof p["text"] === "string" && p["text"]) parts.push(`«${p["text"]}»`);
  return parts.join(" · ");
}

export function TaskList({
  tasks,
  topicNames,
  limit = 10,
}: {
  tasks: CollectTask[];
  /** Имена тематик по id — на общем экране задача ссылается на свою тематику. */
  topicNames?: Map<number, string>;
  limit?: number;
}) {
  const shown = tasks.slice(0, limit);
  return (
    <ul className="divide-y divide-border">
      {shown.map((t) => {
        const result = resultText(t);
        const params = paramsText(t);
        const topicName = t.topic_id !== null ? topicNames?.get(t.topic_id) : undefined;
        return (
          <li key={t.id} className="grid gap-1 px-3 py-2 text-[12.5px]">
            <div className="flex flex-wrap items-center gap-2">
              <TaskBadge status={t.status} />
              <span className="font-medium text-fg">{t.kind_name || t.kind}</span>
              {topicName && t.topic_id !== null ? (
                <Link to={`/trends/${t.topic_id}`} className="text-accent-text hover:underline">
                  {topicName}
                </Link>
              ) : null}
              {t.kind === "pinterest" && t.query_id !== null && topicNames ? (
                <Link to={`/trends/type/${t.query_id}`} className="text-accent-text hover:underline">
                  тип #{t.query_id}
                </Link>
              ) : null}
              {params ? <span className="text-fg-muted">{params}</span> : null}
              <span className="ml-auto whitespace-nowrap text-[11.5px] text-fg-faint tnum">
                {t.author ? `${t.author} · ` : ""}
                {when(t.date_create)}
                {t.date_finish ? ` → ${when(t.date_finish)}` : t.date_start ? ` · начат ${when(t.date_start)}` : ""}
              </span>
            </div>
            {result && t.status === "done" ? <div className="text-[12px] text-fg-muted">{result}</div> : null}
            {t.error ? (
              <div className="whitespace-pre-wrap break-words rounded-[4px] bg-err-bg px-2 py-1 text-[12px] text-err">
                {t.error}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
